/**
 * TanStack Query mutation for creating a new KYC form via tRPC.
 */

import { transformDBKYBToKYCFormData } from "../services/data/api/kycProvider";
import { trpc } from "../trpc";
import type { KYCFormData } from "../types";

function createEmptyForm(): KYCFormData {
	const now = new Date().toISOString();
	return {
		id: `kyc-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`,
		currentStep: 1,
		percentComplete: 0,
		status: "draft",
		lastSavedAt: now,
		createdAt: now,
		updatedAt: now,
		businessLegalName: "",
		dba: "",
		previousBusinessNames: "",
		entityType: "",
		countryOfIncorporation: "",
		registrationNumber: "",
		dateOfIncorporation: "",
		registeredAddress: {
			address1: "",
			address2: "",
			city: "",
			stateProvince: "",
			zip: "",
			country: "",
		},
		website: "",
		businessDescription: "",
		expectedMonthlyVolume: 0,
		federalTaxId: "",
		vatId: "",
		rutDocument: "",
		walletType: "Custodial",
		hostedStatus: "Hosted",
		currentlyIntegratedWithStellar: false,
		allowP2PInternal: false,
		allowP2PExternal: false,
		virtualAssetsDetails: "",
		publicKeySharing: "Individual",
		isWalletOwnerKYCRequired: false,
		walletHolderInfoCollected: "",
		isWalletHolderInfoUpdatedRegularly: false,
		otherFundingMethods: "",
		numberOfOwnershipTiers: 1,
		ownershipTiers: [],
		consentForBackgroundChecks: false,
		anyOwnerConvicted: false,
		boardOfDirectors: [],
		isRegulatedEntity: false,
		licenses: [],
		complianceOfficer: {
			name: "",
			email: "",
			phone: "",
			role: "Compliance Officer",
		},
		sanctionsScreening: false,
		blockchainMonitoring: false,
		regulatoryEnforcement: false,
		velocityLimits: "",
		transactionLimits: "",
		otherLimits: "",
		blockWalletHolders: false,
		primaryBusinessContact: { name: "", email: "", phone: "" },
		primaryProductContact: { name: "", email: "", phone: "" },
		primaryLegalContact: { name: "", email: "", phone: "" },
		primarySigner: { name: "", email: "", phone: "", role: "" },
	};
}

export function useKYBCreate() {
	const utils = trpc.useUtils();

	const mutation = trpc.kyb.create.useMutation({
		onSuccess: () => {
			utils.kyb.list.invalidate();
		},
	});

	return {
		...mutation,
		mutate: (walletId?: string) => {
			mutation.mutate({ wallet_id: walletId ?? null });
		},
		mutateAsync: async (walletId?: string) => {
			const created = await mutation.mutateAsync({
				wallet_id: walletId ?? null,
			});
			return transformDBKYBToKYCFormData(created);
		},
	};
}
